// Ekran 05 — ünitenin tam sayfa detay sekmesi.
//
// ref_files/btys-erp-05-uniteler.html:591-628 `fullHTML()` + :582-590 `prevRow()`
// karşılığı. Üç giriş noktası buraya bağlanır (referans :516 satır çift tık,
// :523 satır sonu ↗, :563 panel altlığı ↗).
//
// Sınıf adları referanstan aynen alındı (§3.2); yeni CSS kuralı eklenmedi.

import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from '../../components/Toast'
import { ApiHatasi } from '../../lib/api'
import { sayiyaCevirBos, tr } from '../../lib/trFormat'
import { useYetki } from '../../lib/yetki'
import { FrekSecici } from './SablonDrawer'
import {
  FREK_ETIKET,
  GRUP_ETIKET,
  frekKirilimi,
  frekOzeti,
  uniteler as uniteApi,
  type BeyanParametresiDto,
  type ParametreIstek,
  type ParametreKaynagi,
} from './api'

/** Referans :609 — parametrenin nereden beslendiği. */
const KAYNAK_ETIKET: Record<ParametreKaynagi, string> = {
  lab: 'Laboratuvar (K21)',
  olay: 'Olay kaydı',
  manuel: 'Manuel giriş',
}

/** Önizleme 00–07 saatlerini gösterir, gerisini "…23" ile kısaltır (referans :616). */
const ONIZLEME_SAATLERI = [0, 1, 2, 3, 4, 5, 6, 7]

/**
 * Referans `prevRow()` (:582-590): Üretim Beyanı gridinde bu parametrenin hangi
 * saatlerde hücre açtığını gösterir. Salt-okunur — girişler ekran 06'da yapılır.
 */
function OnizlemeSatiri({ p }: { p: BeyanParametresiDto }) {
  const f = Number.parseInt(p.frek, 10)
  return (
    <tr>
      <td className="pl">
        {p.ad} {p.birim ? <span className="bpu">{p.birim}</span> : null}
      </td>
      {ONIZLEME_SAATLERI.map((h) => {
        if (p.frek === 'olay') {
          return (
            <td className="olay" key={h}>
              {h === 0 ? '+' : ''}
            </td>
          )
        }
        if (p.frek === 'lab') {
          return (
            <td className="off" key={h}>
              ·
            </td>
          )
        }
        if (h % f === 0) return <td className="on" key={h} />
        return (
          <td className="off" key={h}>
            —
          </td>
        )
      })}
      <td className="pl" style={{ color: 'var(--faint)' }}>
        …23
      </td>
    </tr>
  )
}

export default function UniteDetaySekmesi({
  uniteId,
  kapat,
}: {
  uniteId: string
  /** Ünite kaldırıldığında sekmeyi kapatır — sahipsiz sekme kalmasın. */
  kapat?: () => void
}) {
  const kuyruk = useQueryClient()
  const { izinVar } = useYetki()
  const yazabilir = izinVar('Uniteler', 'yaz')

  // Sayfayla AYNI queryKey'ler: önbellek paylaşılır, sekme açılışı ek istek doğurmaz.
  const { data: detay } = useQuery({
    queryKey: ['unite', uniteId],
    queryFn: () => uniteApi.detay(uniteId),
  })
  const { data: siniflar = [] } = useQuery({
    queryKey: ['unite-siniflari'],
    queryFn: uniteApi.siniflar,
  })

  const sinif = siniflar.find((s) => s.id === detay?.unite.uniteSinifiId) ?? null

  // Teknik değerlerin sunucudan gelen hali JSON'a çevrilip karşılaştırılıyor:
  // sıklık kaydı sonrası gelen yeniden çekim teknik değerleri DEĞİŞTİRMEDİĞİ
  // sürece kullanıcının yazdığı taslak korunsun.
  const teknikJson = detay ? JSON.stringify(detay.unite.teknikDegerler) : ''
  const temel = useMemo<Record<string, string>>(() => {
    if (!teknikJson) return {}
    // Değerler serbest METİN geliyor — sayı dönüşümünden geçirilmiyor, yoksa
    // "Mezofilik 38–40°C" gibi alanlar girişte boşalır, kaydedince silinirdi.
    const ham = JSON.parse(teknikJson) as Record<string, string | null>
    return Object.fromEntries(Object.entries(ham).map(([k, v]) => [k, v ?? '']))
  }, [teknikJson])

  const [taslak, setTaslak] = useState<{ kaynak: string; alanlar: Record<string, string> }>({
    kaynak: '',
    alanlar: {},
  })
  const alanlar = taslak.kaynak === teknikJson ? taslak.alanlar : temel
  const alanYaz = (anahtar: string, deger: string) =>
    setTaslak({ kaynak: teknikJson, alanlar: { ...alanlar, [anahtar]: deger } })

  const degisti = (sinif?.teknikAlanlar ?? []).some(
    (a) => (alanlar[a.anahtar] ?? '') !== (temel[a.anahtar] ?? ''),
  )

  const kaydet = useMutation({
    mutationFn: () =>
      uniteApi.guncelle(uniteId, {
        kod: detay!.unite.kod,
        ad: detay!.unite.ad,
        uniteSinifiId: detay!.unite.uniteSinifiId,
        durum: detay!.unite.durum,
        teknikDegerler: Object.fromEntries(
          (sinif?.teknikAlanlar ?? []).map((a) => [a.anahtar, alanlar[a.anahtar] ?? '']),
        ),
      }),
    onSuccess: (u) => {
      kuyruk.invalidateQueries({ queryKey: ['uniteler'] })
      kuyruk.invalidateQueries({ queryKey: ['unite'] })
      kuyruk.invalidateQueries({ queryKey: ['unite-ozeti'] })
      toast(`${u.kod} · teknik değerler kaydedildi`)
    },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Ünite kaydedilemedi'),
  })

  const sil = useMutation({
    mutationFn: () => uniteApi.sil(uniteId),
    onSuccess: () => {
      kuyruk.invalidateQueries({ queryKey: ['uniteler'] })
      kuyruk.invalidateQueries({ queryKey: ['unite-ozeti'] })
      toast(`${detay?.unite.kod} kaldırıldı`)
      kapat?.()
    },
    // §8-05: beyan ya da numune kaydı taşıyan ünite silinemez — sunucunun
    // K referanslı 409 mesajı olduğu gibi gösterilir (uydurma toast yok).
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Ünite kaldırılamadı'),
  })

  const frekKaydet = useMutation({
    mutationFn: (d: { parametreler: ParametreIstek[]; ad: string; frek: string }) =>
      uniteApi.parametreler(uniteId, d.parametreler),
    onSuccess: (_, degisken) => {
      // Panel + açık detay sekmesi tek hamlede senkronlansın.
      kuyruk.invalidateQueries({ queryKey: ['uniteler'] })
      kuyruk.invalidateQueries({ queryKey: ['unite'] })
      kuyruk.invalidateQueries({ queryKey: ['unite-ozeti'] })
      kuyruk.invalidateQueries({ queryKey: ['beyan'] })
      toast(`${detay?.unite.kod} · ${degisken.ad} → ${FREK_ETIKET[degisken.frek]} (K23)`)
    },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Sıklık kaydedilemedi'),
  })

  if (!detay) return <div className="crumb">Yükleniyor…</div>

  const u = detay.unite
  const parametreler = detay.parametreler
  const saatlik = parametreler.filter((p) => p.frek === '1').length
  const labAdet = parametreler.filter((p) => p.frek === 'lab').length

  // Kapasite kartı sınıfın 1. teknik alanına bağlı: sözlüğün anahtar sırasına
  // güvenilmiyor, JSON'da sıra garantili değil.
  const kapAlan = sinif?.teknikAlanlar[0] ?? null
  // Değer serbest metin: sayıya çevrilebiliyorsa TR biçimiyle, çevrilemiyorsa
  // ham metin basılır (GA-1 → "Şartlandırıcı çıkışı").
  const kapHam = kapAlan ? (u.teknikDegerler[kapAlan.anahtar] ?? null) : null
  const kapSayi = kapHam === null ? null : sayiyaCevirBos(kapHam)
  const kapMetin = kapSayi !== null ? tr(kapSayi, 2) : (kapHam || '—')
  const kapAlt = kapAlan
    ? [
        kapAlan.birim && kapAlan.birim !== '-' ? kapAlan.birim : null,
        kapAlan.etiket.toLocaleLowerCase('tr'),
      ]
        .filter(Boolean)
        .join(' · ')
    : '—'

  const frekDegistir = (p: BeyanParametresiDto, v: string) => {
    if (!yazabilir) return
    frekKaydet.mutate({
      ad: p.ad,
      frek: v,
      parametreler: parametreler.map((x) => ({
        id: x.id,
        ad: x.ad,
        birim: x.birim,
        frek: x.id === p.id ? v : x.frek,
        kaynak: x.kaynak,
        sira: x.sira,
      })),
    })
  }

  return (
    <>
      <div className="crumb">
        Ana Veri / Üniteler <b>/ {u.ad}</b>
      </div>

      <div className="d-head">
        <h1 className="d-title">{u.ad}</h1>
        <span className="d-kod">
          {u.kod} · {u.sinifAd}
        </span>
        {u.durum === 'bakimda' && <span className="badge b-amber">Bakımda</span>}
        {u.durum === 'pasif' && <span className="badge b-gray">Pasif</span>}
        <div className="page-actions">
          <button
            className="btn btn-danger btn-sm"
            type="button"
            disabled={!yazabilir || sil.isPending}
            title={
              yazabilir
                ? 'Üniteyi kaldır — beyan ya da numune kaydı varsa sunucu engeller (§8-05)'
                : 'Bu modülde yazma yetkiniz yok'
            }
            onClick={() => sil.mutate()}
          >
            Kaldır
          </button>
          <button
            className="btn btn-pri btn-sm"
            type="button"
            disabled={!yazabilir || !degisti || kaydet.isPending}
            title={
              !yazabilir
                ? 'Bu modülde yazma yetkiniz yok'
                : degisti
                  ? 'Teknik değerleri kaydet'
                  : 'Değişiklik yok — kaydedilecek bir şey bulunmuyor'
            }
            onClick={() => kaydet.mutate()}
          >
            {kaydet.isPending ? 'Kaydediliyor…' : 'Kaydet'}
          </button>
        </div>
      </div>

      <div className="kstrip">
        <div className="k">
          <div className="k-l">Kapasite</div>
          <div className="k-v">{kapMetin}</div>
          <div className="k-s">{kapAlt}</div>
        </div>
        <div className="k">
          <div className="k-l">Beyan Parametresi</div>
          <div className="k-v">{u.parametreSayisi}</div>
          <div className="k-s">{frekOzeti(frekKirilimi(parametreler))}</div>
        </div>
        <div className="k">
          <div className="k-l">Saatlik Alan</div>
          <div className="k-v">{saatlik}</div>
          <div className="k-s">her saat girilir (K4)</div>
        </div>
        <div className="k">
          <div className="k-l">Satır / Gün</div>
          <div className="k-v">{u.gunlukHucre || '—'}</div>
          <div className="k-s">olay bazlı hariç</div>
        </div>
        <div className="k">
          <div className="k-l">Lab Beslemesi</div>
          <div className="k-v">{labAdet || '—'}</div>
          <div className="k-s">salt-okunur (K21)</div>
        </div>
      </div>

      <div className="d-grid">
        <div>
          <div className="card">
            <div className="sect-t">Beyan Parametreleri (B) — sıklık K23’e göre</div>
            <table className="ptab">
              <thead>
                <tr>
                  <th>Parametre</th>
                  <th style={{ width: 90 }}>Birim</th>
                  <th style={{ width: 110 }}>Kaynak</th>
                  <th style={{ width: 140 }}>Sıklık</th>
                  <th className="num" style={{ width: 80 }}>Satır/Gün</th>
                </tr>
              </thead>
              <tbody>
                {parametreler.map((p) => (
                  <tr key={p.id}>
                    <td>{p.ad}</td>
                    <td className="mono" style={{ fontSize: 11 }}>{p.birim ?? '—'}</td>
                    <td className="tur-t">{KAYNAK_ETIKET[p.kaynak]}</td>
                    <td>
                      <FrekSecici
                        deger={p.frek}
                        kaynak={p.kaynak}
                        degisti={(v) => frekDegistir(p, v)}
                      />
                    </td>
                    <td className="num">{p.gunlukAdet || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="pnote">
              Periyodik sıklıklar <b>24’ün bölenleri</b> ile sınırlıdır (1·2·3·4·6·8·12·24) —
              böylece günlük grid tam bölünür (K23).
            </div>
          </div>

          <div className="card" style={{ marginTop: 8 }}>
            <div className="sect-t">Beyan Önizleme — Üretim Beyanı gridinde bu ünite</div>
            <div style={{ padding: '9px 12px' }}>
              <table className="gridprev">
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', paddingLeft: 7 }}>Parametre</th>
                    {ONIZLEME_SAATLERI.map((h) => (
                      <th key={h}>{String(h).padStart(2, '0')}</th>
                    ))}
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {parametreler.map((p) => (
                    <OnizlemeSatiri key={p.id} p={p} />
                  ))}
                </tbody>
              </table>
              <div className="fld-hint" style={{ marginTop: 7 }}>
                Beyaz = giriş hücresi · gri — = periyot dışı · amber + = olay kaydı butonu ·
                nokta = lab’dan salt-okunur akar. Girişler <b>Üretim Beyanı</b> sayfasında
                yapılır, taslak→toplu onay akışıyla (K4/K16).
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="card">
            <div className="sect-t">Teknik Parametreler (T)</div>
            <div style={{ padding: '9px 12px' }}>
              <div className="fgrid">
                {(sinif?.teknikAlanlar ?? []).map((t) => (
                  <div className="fld" key={t.anahtar}>
                    <label>
                      {t.etiket}
                      {t.birim && t.birim !== '-' ? ` (${t.birim})` : ''}
                    </label>
                    <input
                      className="in mono"
                      placeholder="—"
                      readOnly={!yazabilir}
                      value={alanlar[t.anahtar] ?? ''}
                      onChange={(e) => alanYaz(t.anahtar, e.target.value)}
                    />
                  </div>
                ))}
                {(sinif?.teknikAlanlar ?? []).length === 0 && (
                  <div className="cell-sub">Bu sınıfta teknik alan tanımlı değil.</div>
                )}
              </div>
            </div>
          </div>

          {sinif && (
            <div className="inst" style={{ marginTop: 8 }}>
              <div className="inst-t">Sınıf Şablonu — {sinif.ad}</div>
              <div className="inst-row">
                <span className="inst-l">Grup</span>
                <span className="inst-v" style={{ fontSize: 12 }}>{GRUP_ETIKET[sinif.grup]}</span>
              </div>
              <div className="inst-row">
                <span className="inst-l">Varsayılan B seti</span>
                <span className="inst-v">{sinif.parametreler.length}</span>
              </div>
              <div className="inst-note">{sinif.aciklama ?? '—'}</div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
